import { IMonthlyWeightItem, SummaryChartPreparedData } from "../../types/monthlyWasteWeight";
import { numberToWasteType, wasteTypeToBgColor } from "../../utils/enumMapping";
import { WasteType } from "../../utils/enums";

const DEFAULT_WASTE_COLOR = "#88A6D1";

export const CHART_COLORS = {
  separation: "#46AF1A",
  scales: "#88A6D1",
  legendText: "#4A5568",
  hoverLine: "#A0DD87",
  tooltip: {
    background: "rgba(255, 255, 255, 0.95)",
    border: "#E8EEF7",
    title: "#46AF1A",
    text: "#4A5568",
  },
  separationGradient: {
    from: "rgba(100, 212, 53, 0.35)",
    to: "rgba(255, 255, 255, 0)",
  },
};

export const CHART_FONT_SIZE = {
  desktop: 16,
  default: 14,
};

const WASTE_TYPE_ORDER: WasteType[] = [
  WasteType.mixed,
  WasteType.bio,
  WasteType.paper,
  WasteType.plastics,
  WasteType.glass,
  WasteType.metal,
  WasteType.vkm,
  WasteType.dss,
];

export const getMappedWasteType = (wasteType: number): WasteType => numberToWasteType.get(wasteType) ?? WasteType.mixed;

export const getWasteColor = (wasteType: WasteType): string => {
  return wasteTypeToBgColor.get(wasteType) ?? DEFAULT_WASTE_COLOR;
};

export const getWasteLabel = (wasteType: WasteType, t: (key: string, opts?: object) => string): string => {
  return t(`enums.wastetypes.${wasteType}`, {
    defaultValue: wasteType,
  });
};

export const formatKgValue = (value: number, t: (key: string, opts?: object) => string): string => {
  const kg = t("kg");

  if (value >= 1000000) return `${(value / 1000000).toFixed(1)} ${t("scale.million")} ${kg}`;
  if (value >= 10000) return `${Math.round(value / 1000)} ${t("scale.thousand")} ${kg}`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)} ${t("scale.thousand")} ${kg}`;
  return `${value} ${kg}`;
};

export const prepareSummaryChartData = (data: IMonthlyWeightItem[]): SummaryChartPreparedData => {
  const totalByMonth = Array(12).fill(0);
  const separatedByMonth = Array(12).fill(0);
  const monthlyDataByWasteType = new Map<WasteType, number[]>();
  let maxMonthlyWeight = 0;

  data.forEach(({ month, wasteType, totalWeight }) => {
    const index = month - 1;
    if (index < 0 || index > 11) return;

    const mappedType = getMappedWasteType(wasteType);

    totalByMonth[index] += totalWeight;

    if (mappedType !== WasteType.mixed) {
      separatedByMonth[index] += totalWeight;
    }

    if (!monthlyDataByWasteType.has(mappedType)) {
      monthlyDataByWasteType.set(mappedType, Array(12).fill(0));
    }
    const monthData = monthlyDataByWasteType.get(mappedType);
    if (monthData) {
      monthData[index] += totalWeight;
      maxMonthlyWeight = Math.max(maxMonthlyWeight, monthData[index]);
    }
  });

  const degreesOfSeparation = totalByMonth.map((total, index) =>
    total === 0 ? 0 : Math.round((separatedByMonth[index] / total) * 100)
  );

  const wasteTypes = Array.from(monthlyDataByWasteType.keys()).sort(
    (a, b) => WASTE_TYPE_ORDER.indexOf(a) - WASTE_TYPE_ORDER.indexOf(b)
  );

  maxMonthlyWeight = Math.ceil(maxMonthlyWeight * 1.15);

  return { degreesOfSeparation, wasteTypes, monthlyDataByWasteType, maxMonthlyWeight };
};
